import { useCallback, useState } from 'react'
import { useBoundaryPost } from 'shared/boundaryPost'

import { DragEndEvent } from '@dnd-kit/core'

import { useSortableList, Sortable } from './useSortableList'

type useSortableListEditProps<T> = {
	post: (data: T[] | undefined) => void
} & ReturnType<typeof useSortableList<T>>
export const useSortableListEdit = <T,>({
  addRecord,
  editRecord,
  removeRecord,
  moveRecord,
  post
}: useSortableListEditProps<T>) => {
	const [modal, setModal] = useState<{
    command: 'edit' | 'add'
    data?: Sortable<T>
  } | null>(null)

  const closeModal = useCallback(
		() => setModal(null),
		[setModal]
	)
  const onAdd = useCallback(
		() => setModal({ command: 'add' }),
		[setModal]
	)
  const onEditIt = useCallback(
		(data: Sortable<T>) => setModal({ command: 'edit', data }),
		[setModal]
	)

  const { boundaryPost } = useBoundaryPost<T>(post, 500)
  const onSubmitAdd = useCallback(
    (data: T) => {
      const newItems = addRecord(data)
      boundaryPost(newItems)
      closeModal()
    },
    [boundaryPost, closeModal, addRecord]
  )
	const onSubmitEdit = useCallback(
    (data: T) => {
      if (!modal?.data) return
      const newItems = editRecord(modal.data.id, data)
      boundaryPost(newItems)
      closeModal()
    },
    [modal, boundaryPost, closeModal, editRecord]
  )
  const onRemoveIt = useCallback(
    (target: Sortable<T>) => {
      const newItems = removeRecord(target.id)
      boundaryPost(newItems)
    },
    [boundaryPost, removeRecord]
  )
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!event.active || !event.over) return
      const newItems = moveRecord(event.active.id, event.over.id)
      boundaryPost(newItems)
    },
    [boundaryPost, moveRecord]
  )
	return {
		modal, closeModal,
		onAdd, onEditIt, onRemoveIt,
		onSubmitEdit, onSubmitAdd,
		onDragEnd
	}
}